import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { TrendingUp, TrendingDown, Minus, Award, AlertTriangle } from "lucide-react";
import { Card } from "../common/Primitives";

// A curated palette that reads as institutional / professional. Ordered to
// give the largest slice the most-recognisable emerald.
const SLICE_COLORS = [
  "#059669", // emerald 600
  "#0ea5e9", // sky 500
  "#f59e0b", // amber 500
  "#8b5cf6", // violet 500
  "#ef4444", // rose 500
  "#14b8a6", // teal 500
  "#f97316", // orange 500
  "#64748b", // slate 500
];

/**
 * PortfolioInsights
 * -----------------
 * Small analytics tile rendered on the investor dashboard right rail. Shows a
 * donut of capital deployed by sector, aggregate MtM multiple, and winner /
 * loser counts. Falls back to an empty state when no investments are logged.
 */
export default function PortfolioInsights({ insights }) {
  const data = useMemo(() => {
    const rows = (insights?.by_sector || []).filter((s) => s.deployed > 0);
    return rows.map((r, i) => ({
      name: r.sector,
      value: r.deployed,
      pct: r.pct,
      count: r.count,
      color: SLICE_COLORS[i % SLICE_COLORS.length],
    }));
  }, [insights]);

  const total = insights?.total_deployed_inr || 0;
  const currentValue = insights?.total_current_value_inr || 0;
  const multiple = insights?.unrealised_multiple;
  const winners = insights?.winners || 0;
  const losers = insights?.losers || 0;
  const unchanged = insights?.unchanged || 0;

  const gainLoss = currentValue - total;
  const multipleTone = multiple == null ? "neutral"
    : multiple > 1.05 ? "up"
    : multiple < 0.95 ? "down"
    : "flat";

  if ((insights?.total_investments || 0) === 0) {
    return (
      <Card testId="section-portfolio-insights">
        <div className="overline flex items-center gap-1.5 mb-2"><Award size={12} /> Portfolio Insights</div>
        <div className="text-sm text-ink-muted py-4 text-center">
          Log your first investment to unlock insights: sector allocation, MtM multiple, and winners.
        </div>
      </Card>
    );
  }

  return (
    <Card testId="section-portfolio-insights">
      <div className="overline flex items-center gap-1.5 mb-3"><Award size={12} /> Portfolio Insights</div>

      {/* Sector concentration alert (admin-toggleable, threshold configurable) */}
      {(insights?.sector_alerts || []).length > 0 && (
        <SectorAlerts
          alerts={insights.sector_alerts}
          threshold={insights.sector_alert_threshold_pct}
        />
      )}

      {/* Aggregate metrics row */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="border border-navy/10 rounded-md p-2.5">
          <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">Current Value</div>
          <div className="font-heading text-ink text-lg mt-0.5" data-testid="insights-current-value">
            ₹{formatInr(currentValue)}
          </div>
        </div>
        <div className={`border rounded-md p-2.5 ${
          multipleTone === "up" ? "border-emerald-300 bg-emerald-50/40"
          : multipleTone === "down" ? "border-rose-300 bg-rose-50/40"
          : "border-navy/10"
        }`}>
          <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">Multiple</div>
          <div className="font-heading text-lg mt-0.5 flex items-center gap-1" data-testid="insights-multiple">
            {multiple != null ? `${multiple.toFixed(2)}x` : "—"}
            {multipleTone === "up" && <TrendingUp size={14} className="text-emerald-600" />}
            {multipleTone === "down" && <TrendingDown size={14} className="text-rose-600" />}
            {multipleTone === "flat" && <Minus size={14} className="text-ink-muted" />}
          </div>
          {gainLoss !== 0 && (
            <div className={`text-[10px] font-mono ${gainLoss > 0 ? "text-emerald-700" : "text-rose-700"}`}>
              {gainLoss > 0 ? "+" : ""}₹{formatInr(gainLoss)}
            </div>
          )}
        </div>
      </div>

      {/* Winners / Losers counts */}
      <div className="flex items-center gap-3 mb-3 text-xs">
        <span className="inline-flex items-center gap-1 text-emerald-700" data-testid="insights-winners">
          <TrendingUp size={11} /> {winners} up
        </span>
        <span className="inline-flex items-center gap-1 text-rose-700" data-testid="insights-losers">
          <TrendingDown size={11} /> {losers} down
        </span>
        <span className="inline-flex items-center gap-1 text-ink-muted" data-testid="insights-unchanged">
          <Minus size={11} /> {unchanged} flat
        </span>
      </div>

      {/* Sector allocation donut */}
      <div className="border-t border-navy/10 pt-3">
        <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-2">
          Sector Allocation
        </div>
        {data.length === 0 ? (
          <div className="text-xs text-ink-muted italic py-2">No sector data yet.</div>
        ) : (
          <div className="h-52 min-h-[200px]" data-testid="insights-sector-chart">
            <ResponsiveContainer width="100%" height="100%" minWidth={100} minHeight={200}>
              <PieChart>
                <Pie
                  data={data}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={38}
                  outerRadius={68}
                  paddingAngle={2}
                >
                  {data.map((entry, i) => (
                    <Cell key={i} fill={entry.color} stroke="white" strokeWidth={2} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(v, name, p) => [
                    `₹${formatInr(v)} · ${p.payload.pct}%`,
                    p.payload.name,
                  ]}
                  contentStyle={{
                    fontSize: "11px",
                    borderRadius: "6px",
                    border: "1px solid rgba(0,0,0,0.1)",
                    background: "white",
                  }}
                />
                <Legend
                  verticalAlign="bottom"
                  align="center"
                  iconType="circle"
                  wrapperStyle={{ fontSize: "10px", paddingTop: "8px" }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </Card>
  );
}

function SectorAlerts({ alerts, threshold }) {
  // Build a diversify link that pre-excludes every concentrated sector so the
  // marketplace opens showing only under-exposed opportunities.
  const excludeParam = encodeURIComponent(alerts.map((a) => a.sector).join(","));
  const diversifyHref = `/investor/marketplace?sectors_exclude=${excludeParam}`;

  return (
    <div
      className="border border-amber-300 bg-amber-50/50 rounded-md p-2.5 mb-3"
      data-testid="portfolio-sector-alert"
      role="alert"
    >
      <div className="flex items-start gap-2 mb-1.5">
        <AlertTriangle size={13} className="text-amber-700 flex-shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <div className="font-heading text-amber-900 text-xs">Concentration risk</div>
          <div className="text-[11px] text-amber-800 mt-0.5">
            {alerts.length === 1
              ? <><span className="font-heading">{alerts[0].sector}</span> holds <span className="font-heading">{alerts[0].pct}%</span> of your capital — above the <span className="font-heading">{threshold}%</span> threshold.</>
              : <>{alerts.length} sectors exceed the <span className="font-heading">{threshold}%</span> threshold.</>
            }
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
        {alerts.slice(0, 3).map((a) => (
          <span
            key={a.sector}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-widest bg-amber-100 border border-amber-300 text-amber-900"
            data-testid={`portfolio-sector-alert-${a.sector}`}
          >
            {a.sector} · {a.pct}%
          </span>
        ))}
      </div>
      <Link
        to={diversifyHref}
        className="inline-flex items-center gap-1 text-[11px] font-heading text-amber-900 hover:text-amber-950 mt-2"
        data-testid="portfolio-sector-alert-cta"
      >
        Auto-diversify — show other sectors →
      </Link>
    </div>
  );
}

function formatInr(n) {
  if (!n || Number.isNaN(n)) return "0";
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs >= 1e7) return `${sign}${(abs / 1e7).toFixed(2)} Cr`;
  if (abs >= 1e5) return `${sign}${(abs / 1e5).toFixed(1)} L`;
  return sign + abs.toLocaleString("en-IN");
}
